import { lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import MainLayout from '../layouts/main';
// guards
import AuthGuard from '../guards/AuthGuard';
import GuestGuard from '../guards/GuestGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
import { menuConfig, menuPrerelease } from '../layouts/main/MenuConfig';
// components
import LoadingScreen from '../components/LoadingScreen';
import NextRoute from '../components/NextRoute';
// routes
import { adminRoutes } from './adminRoutes';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected/:refered_by', element: <Register /> },
        { path: 'register-success', element: <RegisterSuccess /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password/:uid/:token', element: <NewPassword /> },
        { path: 'verify/:uid/:token', element: <VerifyCode /> },
      ],
    },

    // Campaign landing pages (standalone, no layout)
    {
      path: 'empezar',
      element: (
        <GuestGuard>
          <CampaignRegister />
        </GuestGuard>
      ),
    },

    // Provisional ads review (temporary, remove after campaign launch)
    {
      path: 'provisional-review',
      element: <CampaignAdsReview />,
    },

    // Admin Routes
    adminRoutes,

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Dashboard />, index: true },
        // Legacy routes - can be removed in future
        { path: 'app', element: <GeneralApp /> },
        { path: 'ecommerce', element: <GeneralEcommerce /> },
        { path: 'analytics', element: <GeneralAnalytics /> },
        { path: 'banking', element: <GeneralBanking /> },
        { path: 'booking', element: <GeneralBooking /> },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/account" replace />, index: true },
            { path: 'new', element: <UserCreate /> },
            { path: 'list', element: <UserList /> },
            { path: 'account', element: <UserAccount /> },
            { path: 'store', element: <UserPayments /> },
            { path: ':name/edit', element: <UserCreate /> },
          ],
        },
        { path: 'inbox', element: <Inbox /> },
        {
          path: 'library',
          children: [
            { element: <Navigate to="/dashboard/library/list" replace />, index: true },
            { path: 'list', element: <LibrayList /> },
          ],
        },
        {
          path: 'mycolab',
          children: [
            { element: <Navigate to="recipes" replace />, index: true },
            { path: 'organizations', element: <MycoLabOrganizations /> },
            { path: 'recipes', element: <MycoLabRecipes /> },
            { path: 'recipes/:id', element: <MycoLabRecipeDetail /> },
            { path: 'batches', element: <MycoLabBatches /> },
            { path: 'batches/:id', element: <MycoLabBatchDetail /> },
            { path: 'stages', element: <MycoLabStages /> },
            { path: 'monitoring', element: <MycoLabMonitoring /> },
            { path: 'alerts', element: <MyCoreAlerts /> },
            { path: 'notes', element: <MycoLabNotes /> },
            { path: 'archives', element: <MycoLabArchives /> },
            { path: 'sensors', element: <MycoLabSensors /> },
            { path: 'api-keys', element: <MycoLabApiKeys /> },
            { path: 'strains', element: <MycoLabLibrary /> },
            { path: 'strains/:id', element: <MycoLabStrainDetail /> },
            { path: 'varieties/:id', element: <MycoLabSpeciesDetail /> },
            { path: 'genetics', element: <Navigate to="/dashboard/mycolab/strains" replace /> },
            { path: 'settings', element: <MycoLabSettings /> },
          ],
        },
        {
          path: 'developers',
          children: [
            { element: <Navigate to="api" replace />, index: true },
            { path: 'api', element: <DashboardApiDocs /> },
          ],
        },
        {
          path: 'gamification',
          children: [
            { element: <GamificationDashboard />, index: true },
            { path: 'achievements', element: <AchievementsPage /> },
            { path: 'leaderboard', element: <LeaderboardPage /> },
            { path: 'challenges', element: <ChallengesPage /> },
          ],
        },

        {
          path: 'shop',
          children: [
            { element: <Navigate to="/dashboard/shop/checkout" replace />, index: true },
            { path: 'checkout', element: <Checkout /> },
            { path: 'products', element: <Products /> },
            { path: 'orders', element: <MyOrders /> },
            { path: 'memberships', element: <Memberships /> },
            { path: 'membership/success', element: <MembershipSuccess /> },
            { path: 'membership/error', element: <MembershipError /> },
            { path: 'payment/memberships', element: <Payment /> },
            { path: 'pricing', element: <DashboardPricing /> },
          ],
        },
        {
          path: 'mail',
          children: [
            { element: <Navigate to="/dashboard/mail/all" replace />, index: true },
            { path: 'label/:customLabel', element: <Mail /> },
            { path: 'label/:customLabel/:mailId', element: <Mail /> },
            { path: ':systemLabel', element: <Mail /> },
            { path: ':systemLabel/:mailId', element: <Mail /> },
          ],
        },
        {
          path: 'chat',
          children: [
            { element: <Chat />, index: true },
            { path: 'new', element: <Chat /> },
            { path: ':conversationKey', element: <Chat /> },
          ],
        },
        {
          path: 'support',
          children: [
            { element: <Navigate to="/dashboard/support/chat" replace />, index: true },
            { path: 'chat', element: <SupportChat /> },
            { path: 'guide', element: <SupportGuide /> }
          ],
        },
        {
          path: 'settings',
          children: [
            { element: <Settings />, index: true },
            { path: 'telegram', element: <TelegramSettings /> },
            { path: 'language', element: <LanguageSettings /> },
          ],
        },
        { path: 'calendar', element: <Calendar /> },
        { path: 'kanban', element: <Kanban /> },
        { path: 'permission-denied', element: <PermissionDenied /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'pricing', element: <Pricing /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <MainLayout navConfig={menuConfig} />,
      children: [
        // Public pages served by Next in production: see components/NextRoute.js
        { element: <NextRoute><HomePage /></NextRoute>, index: true },
        { path: 'mycolab', element: <NextRoute><MycoLabPage /></NextRoute> },
        { path: 'about-us', element: <NextRoute><About /></NextRoute> },
        { path: 'contact-us', element: <NextRoute><Contact /></NextRoute> },
        { path: 'faqs', element: <NextRoute><Faqs /></NextRoute> },
        { path: 'privacy', element: <NextRoute><Privacy /></NextRoute> },
        { path: 'terms', element: <NextRoute><Terms /></NextRoute> },
        // Google Play requires a public account-deletion URL for any app
        // that lets people create an account.
        { path: 'delete-account', element: <DeleteAccount /> },
        // The mobile app's Settings screen links to /help.
        { path: 'help', element: <Faqs /> },
        { path: 'face/privacy', element: <FacebookPrivacy /> },
        {
          path: 'tools',
          children: [
            { element: <NextRoute><Navigate to="calc" replace /></NextRoute>, index: true },
            { path: 'calc', element: <NextRoute><CalcOverview /></NextRoute> },
            { path: 'mushroom-map', element: <MushroomMapOverview /> },
            { path: 'quiz', element: <QuizOverview /> },
            { path: 'quiz/:quizId', element: <QuizOverview /> },
            { path: 'cultivation-recipes', element: <NextRoute><CultivationRecipes /></NextRoute> },
            { path: 'cultivation-recipes/:shortId', element: <NextRoute><RecipeDetail /></NextRoute> },
            { path: 'wood-guide', element: <NextRoute><WoodGuideOverview /></NextRoute> },
            // One page per mushroom / contaminant exists only in Next; without Next the SPA shows the guide.
            { path: 'wood-guide/:slug', element: <NextRoute><WoodGuideOverview /></NextRoute> },
            { path: 'contamination-guide', element: <NextRoute><ContaminationGuide /></NextRoute> },
            { path: 'contamination-guide/:slug', element: <NextRoute><ContaminationGuide /></NextRoute> },
            { path: 'tincture-calc', element: <NextRoute><TinctureCalcOverview /></NextRoute> },
          ]
        },
        {
          path: 'blog',
          children: [
            { element: <NextRoute><Blog /></NextRoute>, index: true },
            { path: 'posts', element: <BlogPosts /> },
            { path: 'post/:slug', element: <NextRoute><BlogPost /></NextRoute> },
            { path: 'new', element: <BlogNewPost /> },
          ],
        },
        {
          path: 'shop',
          children: [
            { element: <NextRoute><Shop /></NextRoute>, index: true, },
            { path: 'products/:product_code', element: <NextRoute><ProductDetail /></NextRoute> },
            { path: 'checkout', element: <Checkout /> },
            { path: 'checkout/success', element: <CheckoutSuccess /> },
            { path: 'checkout/error', element: <CheckoutError /> },
            { path: 'track-order', element: <TrackOrder /> },
          ]
        },
        {
          path: 'community',
          children: [
            { path: 'photos', element: <CommunityGallery /> },
          ]
        },
        {
          path: 'developers',
          children: [
            { element: <Navigate to="api" replace />, index: true },
            { path: 'api', element: <PublicApiDocs /> },
          ]
        }
      ],
    },

    // Prerelease Routes
    {
      path: 'prerelease',
      element: <MainLayout navConfig={menuPrerelease} />,
      children: [
        { element: <PrereleasePage />, index: true },
        { path: 'prerelease/refered_by/:referer', element: <PrereleasePage /> },
      ]
    },

    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const RegisterSuccess = Loadable(lazy(() => import('../pages/auth/RegisterSuccess')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));

// PUBLIC PAGES
const CommunityGallery = Loadable(lazy(() => import('../pages/CommunityGallery')));

// DASHBOARD ----------------------------------------------------------------------

// OVERVIEW TOOLS
const CalcOverview = Loadable(lazy(() => import('../pages/tools/Calc')));
const MushroomMapOverview = Loadable(lazy(() => import('../pages/tools/MushroomMap')));
const QuizOverview = Loadable(lazy(() => import('../pages/tools/Quiz')));
const CultivationRecipes = Loadable(lazy(() => import('../pages/tools/CultivationRecipes')));
const RecipeDetail = Loadable(lazy(() => import('../pages/tools/RecipeDetail')));
const WoodGuideOverview = Loadable(lazy(() => import('../pages/tools/WoodGuide')));
const ContaminationGuide = Loadable(lazy(() => import('../pages/tools/ContaminationGuide')));
const TinctureCalcOverview = Loadable(lazy(() => import('../pages/tools/TinctureCalc')));

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));
const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));

// USER
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserPayments = Loadable(lazy(() => import('../pages/dashboard/user/UserPayments')));

// SOURCE LIBRARY
const LibrayList = Loadable(lazy(() => import('../pages/dashboard/library/LibraryList')));

// MYCOLAB
const Dashboard = Loadable(lazy(() => import('../pages/dashboard/Dashboard')));
const MycoLabOrganizations = Loadable(lazy(() => import('../pages/dashboard/mycolab/Organizations')));
const MycoLabRecipes = Loadable(lazy(() => import('../pages/dashboard/mycolab/Recipes')));
const MycoLabRecipeDetail = Loadable(lazy(() => import('../pages/dashboard/mycolab/RecipeDetail')));
const MycoLabBatches = Loadable(lazy(() => import('../pages/dashboard/mycolab/Batches')));
const MycoLabBatchDetail = Loadable(lazy(() => import('../pages/dashboard/mycolab/BatchDetail')));
const MycoLabStages = Loadable(lazy(() => import('../pages/dashboard/mycolab/Stages')));
const MycoLabMonitoring = Loadable(lazy(() => import('../pages/dashboard/mycolab/Monitoring')));
const MyCoreAlerts = Loadable(lazy(() => import('../pages/dashboard/mycore/Alerts')));
const MycoLabNotes = Loadable(lazy(() => import('../pages/dashboard/mycolab/Notes')));
const MycoLabArchives = Loadable(lazy(() => import('../pages/dashboard/mycolab/Archives')));
const MycoLabSensors = Loadable(lazy(() => import('../pages/dashboard/mycolab/SensorConfiguration')));
const MycoLabApiKeys = Loadable(lazy(() => import('../pages/dashboard/mycolab/ApiKeys')));
const MycoLabLibrary = Loadable(lazy(() => import('../pages/dashboard/mycolab/MyLibrary')));
const MycoLabSpeciesDetail = Loadable(lazy(() => import('../pages/dashboard/mycolab/SpeciesDetail')));
const MycoLabStrainDetail = Loadable(lazy(() => import('../pages/dashboard/mycolab/StrainDetail')));
const MycoLabSettings = Loadable(lazy(() => import('../pages/dashboard/mycolab/Settings')));

// DEVELOPERS
const DashboardApiDocs = Loadable(lazy(() => import('../pages/dashboard/developers/ApiDocs')));
const PublicApiDocs = Loadable(lazy(() => import('../pages/developers/ApiDocs')));

// GAMIFICATION
const GamificationDashboard = Loadable(lazy(() => import('../pages/dashboard/gamification/GamificationDashboard')));
const AchievementsPage = Loadable(lazy(() => import('../pages/dashboard/gamification/AchievementsPage')));
const LeaderboardPage = Loadable(lazy(() => import('../pages/dashboard/gamification/LeaderboardPage')));
const ChallengesPage = Loadable(lazy(() => import('../pages/dashboard/gamification/ChallengesPage')));

// SHOP
const Payment = Loadable(lazy(() => import('../pages/dashboard/shop/Payment')));
const Checkout = Loadable(lazy(() => import('../pages/shop/Checkout')));
const CheckoutSuccess = Loadable(lazy(() => import('../pages/shop/CheckoutSuccess')));
const CheckoutError = Loadable(lazy(() => import('../pages/shop/CheckoutError')));
const Products = Loadable(lazy(() => import('../pages/dashboard/shop/Products')));
const ProductDetail = Loadable(lazy(() => import('../pages/dashboard/shop/ProductDetail')));
const Memberships = Loadable(lazy(() => import('../pages/dashboard/shop/Memberships')));
const DashboardPricing = Loadable(lazy(() => import('../pages/dashboard/shop/Pricing')));
const MembershipSuccess = Loadable(lazy(() => import('../pages/dashboard/shop/MembershipSuccess')));
const MembershipError = Loadable(lazy(() => import('../pages/dashboard/shop/MembershipError')));
const MyOrders = Loadable(lazy(() => import('../pages/dashboard/shop/MyOrders')));
const TrackOrder = Loadable(lazy(() => import('../pages/shop/TrackOrder')));

// SUPPORT
const SupportChat = Loadable(lazy(() => import('../pages/dashboard/support/Chat')));

// SETTINGS
const Settings = Loadable(lazy(() => import('../pages/dashboard/settings/Settings')));
const TelegramSettings = Loadable(lazy(() => import('../pages/dashboard/settings/TelegramSettings')));
const LanguageSettings = Loadable(lazy(() => import('../pages/dashboard/settings/LanguageSettings')));

// MESSAGES
const Inbox = Loadable(lazy(() => import('../pages/Inbox')));
const SupportGuide = Loadable(lazy(() => import('../pages/dashboard/support/Guide')));

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/PermissionDenied')));

// APP
const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));

// PAGE ----------------------------------------------------------------------

// BLOG
const Blog = Loadable(lazy(() => import('../pages/Blog')));
const BlogPost = Loadable(lazy(() => import('../pages/BlogPost')));
const BlogPosts = Loadable(lazy(() => import('../pages/BlogPosts')));
const BlogNewPost = Loadable(lazy(() => import('../pages/BlogNewPost')));

// DIRECTORY
const Shop = Loadable(lazy(() => import('../pages/Shop')));


// PRERELEASE
const PrereleasePage = Loadable(lazy(() => import('../pages/Prerelease')));

// CAMPAIGN LANDING PAGES
const CampaignRegister = Loadable(lazy(() => import('../pages/CampaignRegister')));
const CampaignAdsReview = Loadable(lazy(() => import('../pages/CampaignAdsReview')));

// MAIN
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const MycoLabPage = Loadable(lazy(() => import('../pages/MycoLab')));
const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Privacy = Loadable(lazy(() => import('../pages/Privacy')));
const Terms = Loadable(lazy(() => import('../pages/Terms')));
const DeleteAccount = Loadable(lazy(() => import('../pages/DeleteAccount')));
const FacebookPrivacy = Loadable(lazy(() => import('../pages/FacebookPrivacy')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
